import React, { useState } from "react";
import { Button, Tooltip } from "@altimateai/lego";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getOpportunitiesRedirection } from "@/helpers/apis";
import ResourceTag from "@/pages/explorer/components/resourceTag/ResourceTag";
import styles from "@/pages/query/styles.module.scss";

export const getResourceOpportunityUrl = ({
  url,
  resource_type,
  opportunity_id,
  startDate,
  endDate,
}: {
  url: string;
  resource_type?: number | null;
  opportunity_id: string;
  startDate?: Date;
  endDate?: Date;
}) => {
  const searchParams: Record<string, string> = {
    start_date: startDate?.toISOString() || "",
    end_date: endDate?.toISOString() || "",
    opportunityId: opportunity_id,
  };
  if (resource_type === 2) searchParams["tab"] = "2";
  return `${url}?${new URLSearchParams(searchParams).toString()}`;
};

interface ResourcesProps {
  resources: Array<{
    resource_uri: string;
    resource_name: string;
    resource_type: number;
  }>;
  startDate?: Date;
  endDate?: Date;
  instance_id: string;
  opportunity_id: string;
}

export const Resources: React.FC<ResourcesProps> = ({
  resources,
  startDate,
  endDate,
  instance_id,
  opportunity_id,
}) => {
  const navigate = useNavigate();
  const [tooltipContent, setTooltipContent] = useState<Record<string, string>>({});
  const { mutate } = useMutation({
    mutationFn: getOpportunitiesRedirection,
    onSuccess: (data, variables) => {
      if (!data?.url) {
        setTooltipContent(prev => ({
          ...prev,
          [variables?.resource_uri || ""]: "Resource not found",
        }));
        return;
      }
      navigate(
        getResourceOpportunityUrl({
          resource_type: variables?.resource_type,
          opportunity_id,
          url: data.url,
          startDate,
          endDate,
        })
      );
    },
  });
  const [shownResources, setShownResources] = useState(resources.slice(0, 3));
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.resources}>
      {shownResources.map((resource, i) => (
        <Tooltip
          key={resource.resource_uri}
          title={
            tooltipContent[resource.resource_uri || ""] ||
            resource.resource_uri ||
            resource.resource_name
          }
        >
          <ResourceTag
            resource={resource}
            index={i}
            onClick={() =>
              mutate({
                resource_type: resource.resource_type,
                resource_uri: resource.resource_uri,
                resource_name: resource.resource_name,
                resource_id: null,
                instance_id,
                opportunity_id,
              })
            }
          />
        </Tooltip>
      ))}
      {resources.length > 3 && (
        <Button
          className={styles.more}
          onClick={e => {
            e.stopPropagation();
            setExpanded(!expanded);
            setShownResources(expanded ? resources.slice(0, 3) : resources);
          }}
          variant="link"
        >
          {expanded ? "Less" : "More"}
        </Button>
      )}
    </div>
  );
};
